import styled from "@emotion/styled";
import DOMPurify from "dompurify";
import React from "react";
import { ChevronRightIcon } from "../../../utils/icones";
import MainV2TopBanner from "./MainV2TopBanner";

export interface MainBannerProps {
  id: string;
  theme: "branded-gradient" | "dark-slate" | "warning" | "fatal";
  title: string;
  description?: string;
  leading?: React.ReactNode;
  trailing?: { type: "button"; label: string } | { type: "chevron" };
  onClick?: () => void;
}

export default function MainBanner(props: MainBannerProps) {
  const trailing = (() => {
    if (props.trailing === undefined) {
      return undefined;
    }

    switch (props.trailing.type) {
      case "button":
        return <Button>{props.trailing.label}</Button>;
      case "chevron":
        return <ChevronRightIcon fontSize={24} />;
    }
  })();

  return (
    <Root data-theme={props.theme} onClick={props.onClick}>
      {props.leading !== undefined && <LeadingSlot>{props.leading}</LeadingSlot>}
      <TextSlot>
        <Title dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.title) }} />
        <Description
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.description ?? "") }}
        />
      </TextSlot>
      {trailing !== undefined && <TrailingSlot>{trailing}</TrailingSlot>}
    </Root>
  );
}

const LeadingSlot = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 20px;
    height: 20px;
  }
`;

const TextSlot = styled.div`
  flex: 1;
`;

const TrailingSlot = styled.div`
  height: 24px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  color: var(--title-color);
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Description = styled.p`
  color: var(--description-color);
  margin: 0;
  padding: 0;
`;

const Button = styled.button`
  height: 36px;
  padding: 0 16px;
  margin: -8px 0;
  border: 1px solid var(--button-border-color);
  background-color: transparent;
  border-radius: 32px;
  color: white;
  font-family: inherit;
  font-weight: 700
  font-size: 14px;
`;

const Root = styled(MainV2TopBanner)`
  color: var(--description-color);

  &[data-theme="branded-gradient"] {
    background-image: linear-gradient(
      -45deg,
      #8910e8 0%,
      #0a77e6 33.33%,
      #0d76e6 66.66%,
      #940dff 100%
    );

    --title-color: white;
    --description-color: rgba(255, 255, 255, 0.7);
    --button-border-color: white;
  }

  &[data-theme="dark-slate"] {
    background-color: var(--tw-slate-900);
    --title-color: white;
    --description-color: rgba(255, 255, 255, 0.7);
    --button-border-color: var(--tw-slate-600);
  }

  &[data-theme="warning"] {
    background-color: var(--tw-amber-600);
    background-color: var(--tw-yellow-300);
    --title-color: var(--tw-slate-900);
    --description-color: var(--tw-slate-700);
    --button-border-color: white;
  }

  &[data-theme="fatal"] {
    background-color: var(--tw-red-600);
    --title-color: white;
    --description-color: rgba(255, 255, 255, 0.7);
    --button-border-color: white;
  }

  flex-shrink: 0;
`;
